import React, { useCallback, useMemo } from 'react';
import { useClientUser } from '@/hooks';
import { colors } from '@/styles';
import { useMutation } from '@apollo/client';
import {
    DELETE_SUBREGION,
    DELETE_ZIP_MAPPINGS,
    INSERT_SUBREGION,
    INSERT_SUBREGIONS_MANY,
    INSERT_SUBREGIONS_MANY_WITH_CARRIERS,
    INSERT_SUBREGION_WITH_CARRIERS,
    INSERT_ZIP_MAPPINGS,
    UPDATE_SUBREGION,
} from './graphql/mutations';
import { captureException } from '@sentry/react';
import { ROUTE_COLORS } from '../DispatchPlan/constants';

export const useSubregionState = ({ subregions, selected, hovered, filter }) => {
    const selectedSubregion = useMemo(() => {
        return subregions.find((subregion) => subregion.subregion_id === selected.subregion);
    }, [selected, subregions]);

    const selectedZips = useMemo(() => {
        return Object.entries(selected.zip || {})
            .filter(([, selected]) => selected)
            .map(([zip]) => zip);
    }, [selected]);

    const hoveredSubregion = useMemo(() => {
        return subregions.find((subregion) => subregion.subregion_id === hovered.subregion);
    }, [hovered, subregions]);

    const hoveredZips = useMemo(() => {
        return hoveredSubregion?.zips.map((zip) => zip.zip) || [];
    }, [hoveredSubregion]);

    const includedZips = useMemo(() => {
        return subregions.reduce((acc, subregion) => {
            return [...acc, ...subregion.zips.map((zip) => zip.zip)];
        }, []);
    }, [subregions]);

    return {
        selectedSubregion,
        selectedZips,
        hoveredSubregion,
        hoveredZips,
        hoveredSubregionZip: hovered?.zip,
        includedZips,
        filter,
        highlightOverride: selected.subregion === 'ALL',
    };
};

export const useSubregionCallbacks = (state, callbacks) => {
    const [addSubregionsMany] = useMutation(INSERT_SUBREGIONS_MANY);
    const [addSubregionsManyWithCarriers] = useMutation(INSERT_SUBREGIONS_MANY_WITH_CARRIERS);
    const importSubregions = useCallback(
        (subregions) => {
            callbacks.setLoading((prev) => ({ ...prev, import: true }));
            const mutation = state.includeCarriers ? addSubregionsManyWithCarriers : addSubregionsMany;

            return mutation({
                variables: {
                    subregions,
                },
                onError: (e) => {
                    console.error(e);
                    captureException(e);
                    callbacks.setNotification({
                        severity: 'error',
                        message: `Error importing subregions: ${e.message}`,
                    });
                },
                update: (cache, { data: { created } }) => {
                    cache.updateQuery(
                        {
                            query: state.query,
                            variables: state.queryVariables,
                        },
                        (data) => {
                            return {
                                ...(data || {}),
                                results: [...(data?.results || []), ...(created.returning || [])],
                            };
                        }
                    );
                },
            }).finally(() => {
                callbacks.setLoading((prev) => ({ ...prev, import: false }));
            });
        },
        [state.query, state.queryVariables, state.includeCarriers]
    );

    const [addSubregion] = useMutation(INSERT_SUBREGION);
    const [addSubregionWithCarriers] = useMutation(INSERT_SUBREGION_WITH_CARRIERS);
    const createSubregion = useCallback(
        (subregion) => {
            callbacks.setLoading((prev) => ({ ...prev, subregions: true }));
            const mutation = state.includeCarriers ? addSubregionWithCarriers : addSubregion;

            return mutation({
                variables: {
                    subregion,
                },
                onError: (e) => {
                    console.error(e);
                    captureException(e);
                    callbacks.setNotification({
                        severity: 'error',
                        message: `Error creating subregion: ${e.message}`,
                    });
                },
                update: (cache, { data: { created } }) => {
                    cache.updateQuery(
                        {
                            query: state.query,
                            variables: state.queryVariables,
                        },
                        (data) => {
                            return {
                                ...data,
                                results: [
                                    ...data.results.filter((subregion) => !subregion.subregion_id.startsWith('temp')),
                                    created,
                                ],
                            };
                        }
                    );
                },
            }).finally(() => {
                callbacks.setLoading((prev) => ({ ...prev, subregions: false }));
            });
        },
        [state.query, state.queryVariables, state.includeCarriers]
    );

    const [removeSubregion] = useMutation(DELETE_SUBREGION);
    const deleteSubregion = useCallback(
        (subregion) => {
            callbacks.setLoading((prev) => ({ ...prev, subregions: true }));
            return removeSubregion({
                variables: {
                    subregion_id: subregion.subregion_id,
                },

                onError: (e) => {
                    console.error(e);
                    captureException(e);
                    callbacks.setNotification({
                        severity: 'error',
                        message: `Error deleting subregion: ${e.message}`,
                    });
                },
                update: (cache, { data: { removed } }) => {
                    cache.updateQuery(
                        {
                            query: state.query,
                            variables: state.queryVariables,
                        },
                        (data) => {
                            return {
                                ...data,
                                results: data.results.filter(
                                    (subregion) => subregion.subregion_id !== removed.subregion_id
                                ),
                            };
                        }
                    );
                },
            }).finally(() => {
                callbacks.setLoading((prev) => ({ ...prev, subregions: false }));
            });
        },
        [state.query, state.queryVariables]
    );

    const [updateSubregionMutation] = useMutation(UPDATE_SUBREGION);
    const updateSubregion = useCallback((subregion) => {
        callbacks.setLoading((prev) => ({ ...prev, subregions: true }));
        const { subregion_id, ...rest } = subregion;
        return updateSubregionMutation({
            variables: {
                update: rest,
                subregion_id,
            },
            onError: (e) => {
                console.error(e);
                captureException(e);
                callbacks.setNotification({
                    severity: 'error',
                    message: `Error updating subregion: ${e.message}`,
                });
            },
        }).finally(() => {
            callbacks.setLoading((prev) => ({ ...prev, subregions: false }));
        });
    }, []);

    const [insertZips] = useMutation(INSERT_ZIP_MAPPINGS);
    const addZips = useCallback(
        (zips) => {
            callbacks.setLoading((prev) => ({ ...prev, zips: true }));
            return insertZips({
                variables: {
                    mappings: zips,
                },
                onError: (e) => {
                    console.error(e);
                    captureException(e);
                    callbacks.setNotification({
                        severity: 'error',
                        message: `Error adding zips: ${e.message}`,
                    });
                },
                update: (cache, { data: { created } }) => {
                    cache.updateQuery(
                        {
                            query: state.query,
                            variables: state.queryVariables,
                        },
                        (data) => {
                            const results = [...data.results];
                            const idx = results.findIndex(
                                (subregion) => subregion.subregion_id === created.returning[0].subregion_id
                            );
                            const clone = { ...results[idx] };
                            clone.zips = [...clone.zips, ...created.returning];
                            results[idx] = clone;

                            return { ...data, results };
                        }
                    );
                },
            }).finally(() => {
                callbacks.setLoading((prev) => ({ ...prev, zips: false }));
                callbacks.selectObject((prev) => ({ ...prev, zip: {} }));
            });
        },
        [state.query, state.queryVariables]
    );

    const [removeZipMappings] = useMutation(DELETE_ZIP_MAPPINGS);
    const removeZips = useCallback(
        (mappings) => {
            callbacks.setLoading((prev) => ({ ...prev, zips: true }));
            return removeZipMappings({
                variables: {
                    mappings: mappings.map((mapping) => mapping.mapping_id),
                },
                onError: (e) => {
                    console.error(e);
                    captureException(e);
                    callbacks.setNotification({
                        severity: 'error',
                        message: `Error removing zips: ${e.message}`,
                    });
                },
                update: (cache, { data: { removed } }) => {
                    cache.updateQuery(
                        {
                            query: state.query,
                            variables: state.queryVariables,
                        },
                        (data) => {
                            const removedZips = removed.returning.reduce((acc, removedZip) => {
                                return {
                                    ...acc,
                                    [removedZip.subregion_id]: {
                                        ...(acc[removedZip.subregion_id] || {}),
                                        [removedZip.zip]: true,
                                    },
                                };
                            }, {});

                            return {
                                ...data,
                                results: [...data.results].map((subregion) => {
                                    return {
                                        ...subregion,
                                        zips: subregion.zips.filter(
                                            (mapping) => !removedZips[mapping.subregion_id]?.[mapping.zip]
                                        ),
                                    };
                                }),
                            };
                        }
                    );
                },
            }).finally(() => {
                callbacks.setLoading((prev) => ({ ...prev, zips: false }));
                callbacks.selectObject((prev) => ({ ...prev, zip: {} }));
            });
        },
        [state.query, state.queryVariables]
    );

    const toggleSubregion = useCallback((id) => {
        callbacks.selectObject((prev) => {
            return {
                ...prev,
                subregion: id === prev.subregion ? null : id,
            };
        });
    }, []);

    const toggleZips = useCallback((zips) => {
        callbacks.selectObject((prev) => {
            const updates = Object.fromEntries(zips.map((zip) => [zip, !prev.zip[zip]]));

            return {
                ...prev,
                zip: {
                    ...(prev.zip || {}),
                    ...updates,
                },
            };
        });
    }, []);

    const toggleSubregionHover = useCallback((id) => {
        callbacks.hoverObject((prev) => {
            return {
                ...prev,
                subregion: id === prev.subregion ? null : id,
            };
        });
    }, []);

    const toggleZipHover = useCallback((id) => {
        callbacks.hoverObject((prev) => {
            return {
                ...prev,
                zip: id === prev.zip ? null : id,
            };
        });
    }, []);

    const subregionColors = useMemo(() => {
        return Object.fromEntries(
            (state.subregions || []).map((subregion, i) => [
                subregion.subregion_id,
                ROUTE_COLORS[i % ROUTE_COLORS.length],
            ])
        );
    }, [state.subregions]);

    return {
        // Subregions
        importSubregions,
        createSubregion,
        deleteSubregion,
        updateSubregion,

        // Zips
        addZips,
        removeZips,
        removeZip: (mapping) => removeZips([mapping]),

        // Toggles
        toggleSubregion,
        toggleZips,
        toggleSubregionHover,
        toggleZipHover,

        // Misc
        genName: (subregion, idx) => subregion.name || `Subregion ${idx + 1}`,
        genOwner: () => null,
        genColor: (subregion) => subregionColors[subregion?.subregion_id] || colors.greens.primary,
    };
};
